import { normalizeTurkuSlugKey } from "@/lib/turkuSlug";

export type SongLookupEntry = {
  slug: string;
  title: string;
  songId: string;
};

export type SongLookupPayload = {
  version: number;
  byId: Record<string, { s: string; t: string }>;
  bySlug: Record<string, { id: string; t: string }>;
};

let lookupCache: SongLookupPayload | null = null;

export async function loadSongLookup(): Promise<SongLookupPayload> {
  if (lookupCache) {
    return lookupCache;
  }
  const res = await fetch("/data/turku/song-lookup.json", {
    cache: "force-cache",
  });
  if (!res.ok) {
    throw new Error("song_lookup");
  }
  lookupCache = (await res.json()) as SongLookupPayload;
  return lookupCache;
}

/** Resolve mobile/Supabase song id or slug to display + turku path slug. */
export function resolveFavoriteKey(
  key: string,
  lookup: SongLookupPayload,
): SongLookupEntry | null {
  const trimmed = key.trim();
  if (!trimmed) {
    return null;
  }

  const byId = lookup.byId[trimmed];
  if (byId) {
    return { songId: trimmed, slug: byId.s, title: byId.t };
  }

  const normalized = normalizeTurkuSlugKey(trimmed);
  const bySlug =
    lookup.bySlug[normalized] ?? lookup.bySlug[trimmed];
  if (bySlug) {
    return {
      songId: bySlug.id,
      slug: normalized || trimmed,
      title: bySlug.t,
    };
  }

  return null;
}

/** Favori yalnızca web arşivinde (song-lookup) eşleşiyorsa listelenir. */
export function isDisplayableFavorite(
  entry: SongLookupEntry,
  lookup: SongLookupPayload,
): boolean {
  const title = entry.title.trim();
  const slug = entry.slug.trim();
  if (!title || !slug) {
    return false;
  }
  const bySlug = lookup.bySlug[slug];
  if (!bySlug || bySlug.id !== entry.songId) {
    return false;
  }
  return Boolean(lookup.byId[entry.songId]);
}

export function resolveFavoriteKeys(
  keys: string[],
  lookup: SongLookupPayload,
): SongLookupEntry[] {
  const seen = new Set<string>();
  const out: SongLookupEntry[] = [];

  for (const key of keys) {
    const resolved = resolveFavoriteKey(key, lookup);
    if (
      !resolved ||
      seen.has(resolved.songId) ||
      !isDisplayableFavorite(resolved, lookup)
    ) {
      continue;
    }
    seen.add(resolved.songId);
    out.push(resolved);
  }

  out.sort((a, b) => a.title.localeCompare(b.title, "tr"));
  return out;
}

export function getSongIdForSlug(
  slug: string,
  lookup: SongLookupPayload,
): string | null {
  const trimmed = slug.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = normalizeTurkuSlugKey(trimmed);
  const bySlug = lookup.bySlug[normalized] ?? lookup.bySlug[trimmed];
  if (bySlug?.id) {
    return bySlug.id;
  }

  const resolved = resolveFavoriteKey(trimmed, lookup);
  return resolved?.songId ?? null;
}
